<template>
  <div class="drawer">
    <div class="drawer-container">
      <div class="drawer-content with-footer">
        <el-form
          ref="rentItemForm"
          :model="rentItem"
          label-width="100px"
          size="small"
          class="box"
        >
          <fieldset class="mgb15">
            <legend>
              {{ $t("text.baseInfo") }}
              <span class="float-right">
                {{ $t("text.subtotal") }}：{{
                  (rentItem.price * rentItem.quantity) | currency
                }}</span
              >
            </legend>
            <el-row :gutter="20">
              <el-col :span="8">
                <el-form-item
                  :label="$t('rentItem.serialNo')"
                  :prop="'serialNo'"
                  :rules="[
                    {
                      required: true,
                      validator: validateSerialNo,
                      trigger: 'blur',
                    },
                  ]"
                >
                  <el-input
                    size="small"
                    v-model.trim="rentItem.serialNo"
                    :placeholder="$t('prompt.alphabetNumberLine')"
                    clearable
                  ></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item
                  :label="$t('product.productNo')"
                  :prop="'productNo'"
                  :rules="[
                    {
                      required: true,
                      message: $t('rules.required', [$t('product.productNo')]),
                      trigger: 'blur',
                    },
                  ]"
                >
                  <el-autocomplete
                    v-model.trim="rentItem.productNo"
                    :fetch-suggestions="querySearchProductNo"
                    :trigger-on-focus="false"
                    style="width: 100%"
                    @select="handleSelectProductNo"
                  >
                    <template slot-scope="{ item }">
                      <div class="name">{{ item.productNo }}</div>
                      <span class="addr">{{ item.productNoAlias }}</span>
                    </template>
                  </el-autocomplete>
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item
                  :label="$t('product.category')"
                  :prop="'categoryId'"
                  :rules="[
                    {
                      required: true,
                      message: $t('rules.required', [$t('product.category')]),
                      trigger: 'blur',
                    },
                  ]"
                >
                  <el-select
                    size="small"
                    filterable
                    v-model="rentItem.categoryId"
                  >
                    <el-option
                      v-for="item in categories"
                      :key="item.id"
                      :label="item.name"
                      :value="item.id"
                    />
                  </el-select>
                </el-form-item>
              </el-col>
            </el-row>
            <el-row :gutter="20">
              <el-col :span="8">
                <el-form-item :label="$t('product.size')" :prop="'size'">
                  <el-autocomplete
                    size="small"
                    v-model.trim="rentItem.size"
                    :fetch-suggestions="querySearchSize"
                    style="width: 100%"
                    @select="handleSelectSize"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item :label="$t('product.color')" :prop="'color'">
                  <el-autocomplete
                    size="small"
                    v-model.trim="rentItem.color"
                    :fetch-suggestions="querySearchColor"
                    style="width: 100%"
                    @select="handleSelectColor"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item
                  :label="$t('rentItem.price')"
                  :prop="'price'"
                  :rules="[
                    {
                      required: true,
                      message: $t('rules.required', [$t('rentItem.price')]),
                      trigger: 'blur',
                    },
                  ]"
                >
                  <el-input size="small" v-model.trim="rentItem.price">
                  </el-input>
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item
                  :label="$t('text.quantity')"
                  :prop="'quantity'"
                  :rules="[
                    {
                      required: true,
                      message: $t('rules.required', [$t('text.quantity')]),
                      trigger: 'blur',
                    },
                  ]"
                >
                  <el-row>
                    <el-col :span="14">
                      <el-input
                        size="small"
                        v-model.trim="rentItem.quantity"
                      ></el-input>
                    </el-col>
                    <el-col :span="10">
                      <el-select
                        size="small"
                        v-model="rentItem.unit"
                        filterable
                        :placeholder="$t('menu.units')"
                      >
                        <el-option
                          v-for="unit in units"
                          :key="unit"
                          :label="unit"
                          :value="unit"
                        ></el-option>
                      </el-select>
                    </el-col>
                  </el-row>
                </el-form-item>
              </el-col>
              <el-col :span="16">
                <el-form-item
                  :label="$t('rent.rentAt')"
                  :prop="'_rentAt'"
                  :rules="[
                    {
                      required: true,
                      message: $t('rules.required', [$t('rent.rentAt')]),
                      trigger: 'blur',
                    },
                  ]"
                >
                  <el-date-picker
                    size="small"
                    v-model="rentItem._rentAt"
                    type="daterange"
                    align="right"
                    unlink-panels
                    range-separator="-"
                    :start-placeholder="
                      $t('rules.startAt', [$t('rent.rentAt')])
                    "
                    :end-placeholder="$t('rules.endAt', [$t('rent.rentAt')])"
                  />
                </el-form-item>
              </el-col>
            </el-row>
          </fieldset>
          <fieldset class="mgb15">
            <legend>
              {{ $t("product.images") }}
              <svg-icon icon-class="link" @click="linkImage" />
            </legend>
            <dropzone v-model="rentItem.images" multiple />
          </fieldset>
          <fieldset class="mgb15">
            <legend>{{ $t("text.remark") }}</legend>
            <el-input
              v-model="rentItem.remark"
              type="textarea"
              :rows="4"
            ></el-input>
          </fieldset>
        </el-form>
      </div>
    </div>
    <div class="drawer-footer">
      <el-button
        type="primary"
        size="small"
        plain
        :loading="saveDoing"
        @click="saveSubmit"
        >{{ $t("text.submit") }}</el-button
      >
    </div>

    <el-dialog
      :title="$t('action.linkImage')"
      :visible.sync="linkImageDF"
      :close-on-click-modal="false"
      width="50%"
    >
      <el-form ref="linkImageForm" :model="linkImageObj">
        <el-form-item
          :prop="'linkImageStr'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('action.linkImage')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-input
            v-model="linkImageObj.linkImageStr"
            :placeholder="$t('prompt.linkImageExplain')"
            type="textarea"
            :autosize="{ minRows: 4, maxRows: 10 }"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          plain
          :loading="linkImageDoing"
          size="small"
          @click="linkImageSubmit"
          >{{ $t("text.submit") }}</el-button
        >
        <el-button size="small" @click="linkImageDF = false">{{
          $t("text.close")
        }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>
<script>
import axios from "axios";
import Vue from "vue";
import { page } from "@/utils/mixins.js";
import Util from "@/utils/index.js";

import ThumbnailText from "@/components/ThumbnailText";
import RawHtml from "@/components/RawHtml";
import Dropzone from "@/components/Dropzone";

import RentItem from "@/api/rentItem.js";
import Color from "@/api/color.js";
import Size from "@/api/size.js";
import Product from "@/api/product.js";
import Unit from "@/api/unit.js";
import Category from "@/api/category.js";

export default {
  props: {
    id: Number,
  },
  mixins: [page],
  components: {
    ThumbnailText,
    RawHtml,
    Dropzone,
  },
  data() {
    var validateSerialNo = (rule, value, callback) => {
      const self = this;
      if (value === null) {
        callback(
          new Error(this.$t("rules.required", [this.$t("rentItem.serialNo")]))
        );
        return;
      }
      if (!Util.regexAlphabetNumberLine(value)) {
        callback(new Error(this.$t("prompt.regexAlphabetNumberLine")));
        return;
      }
      RentItem.findBySerialNo({ serialNo: value }).then(function (res) {
        if (res.data.code === 200 && res.data.entity.id !== self.rentItem.id) {
          callback(
            new Error(self.$t("rules.exist", [self.$t("rentItem.serialNo")]))
          );
        } else {
          callback();
        }
      });
    };
    return {
      validateSerialNo: validateSerialNo,
      rentItem: {},
      sizes: [],
      colors: [],
      units: [],
      categories: [],
      productNoColors: [],
      productNoSizes: [],
      saveDoing: false,
      // 图片链接
      linkImageDF: false,
      linkImageObj: {
        linkImageStr: null,
      },
      linkImageDoing: false,
    };
  },
  mounted() {
    // 根据id请求bargain
  },
  created() {
    const self = this;
    self.loadingStart();
    axios
      .all([
        RentItem.findById(self.id),
        Size.findByTenantId(),
        Color.findByTenantId(),
        Unit.findByTenantId(),
        Category.productCategory(),
      ])
      .then(
        axios.spread(function (rentItem, sizes, colors, units, categories) {
          if (rentItem.data.code === 200) {
            self.rentItem = rentItem.data.entity;
            Vue.set(self.rentItem, "_rentAt", [
              new Date(self.rentItem.startAt),
              new Date(self.rentItem.endAt),
            ]);
            self.initData();
          } else {
            self.sysError();
          }
          if (sizes.status === 200) {
            self.sizes = sizes.data;
          } else {
            self.sysError();
          }
          if (sizes.status === 200) {
            self.colors = colors.data;
          } else {
            self.sysError();
          }
          if (units.status === 200) {
            self.units = units.data.map((u) => u.name);
          } else {
            self.sysError();
          }
          if (categories.status === 200) {
            self.categories = categories.data;
          } else {
            self.sysError();
          }
          self.loadingEnd();
        })
      );
  },
  methods: {
    // 图片链接
    linkImage() {
      this.linkImageDF = true;
      this.linkImageObj.linkImageStr = null;
    },
    linkImageSubmit() {
      const self = this;
      this.$refs.linkImageForm.validate((valid) => {
        if (valid) {
          const linkImageArr = this.linkImageObj.linkImageStr.split("\n");
          const linkImageArrFilter = linkImageArr.filter((a) => a != null);
          if (self.rentItem.images == null) {
            self.rentItem.images = linkImageArrFilter.join(";");
          } else {
            self.rentItem.images =
              self.rentItem.images + ";" + linkImageArrFilter.join(";");
          }
          this.linkImageDF = false;
        } else {
          return false;
        }
      });
    },
    initData() {
      const self = this;
      Product.findByProductNo({ productNo: self.rentItem.productNo }).then(
        function (res) {
          if (res.data.code === 200) {
            self.productNoColors = res.data.entity.colors;
            self.productNoSizes = res.data.entity.sizes;
          } else {
            self.productNoColors = [];
            self.productNoSizes = [];
          }
        }
      );
    },
    // 货号
    querySearchProductNo(queryString, cb) {
      const self = this;
      //
      self.rentItem.productName = null;
      self.rentItem.color = null;
      self.rentItem.size = null;
      self.rentItem.categoryId = null;
      self.rentItem.categoryName = null;
      self.rentItem.brandId = null;
      self.rentItem.brand = null;
      self.rentItem.images = null;
      self.rentItem.remark = null;
      self.productNoColors = [];
      self.productNoSizes = [];
      //
      if (queryString == null || queryString.length < 1) {
        return;
      }
      Product.vagueFindByProductNo({
        productNo: queryString,
        type: "product",
      }).then(function (res) {
        self.products = res.data;
        if (self.products.length !== 0) {
          for (const p of self.products) {
            Vue.set(p, "value", p.productNo);
          }
        }
        cb(self.products);
      });
    },
    handleSelectProductNo(item) {
      const self = this;
      self.rentItem.productNo = item.productNo;
      self.rentItem.productName = item.name;
      self.rentItem.color = null;
      self.rentItem.size = null;
      self.rentItem.categoryId = item.categoryId;
      self.rentItem.categoryName = item.categoryName;
      self.rentItem.brandId = item.brandId;
      self.rentItem.brand = item.brandName;
      self.rentItem.images = item.images;
      self.rentItem.unit = item.unit;
      self.rentItem.price = item.rentPrice;
      self.rentItem.productPrice = item.salesPrice;
      self.productNoColors = item.colors;
      self.productNoSizes = item.sizes;
    },
    // 颜色
    querySearchColor(queryString, cb) {
      const colors =
        this.productNoColors.length !== 0
          ? this.productNoColors.map((pc) => {
              return { value: pc };
            })
          : this.colors.map((c) => {
              return { value: c.name };
            });
      if (queryString == null) {
        cb(colors);
      } else {
        cb(colors.filter((c) => c.value.indexOf(queryString) !== -1));
      }
    },
    handleSelectColor() {
      this.rentItem.color = Util.jyTrim(this.rentItem.color);
    },
    // 规格
    querySearchSize(queryString, cb) {
      const self = this;
      let sizes = [];
      if (self.productNoSizes.length !== 0) {
        sizes = this.productNoSizes.map((pc) => {
          return { value: pc };
        });
      } else {
        sizes = this.sizes.map((c) => {
          return { value: c.name, explain: c.explain };
        });
      }
      if (queryString == null) {
        cb(sizes);
      } else {
        cb(sizes.filter((c) => c.value.indexOf(queryString) !== -1));
      }
    },
    handleSelectSize() {
      const self = this;
      self.rentItem.size = Util.jyTrim(self.rentItem.size);
    },

    //
    saveSubmit() {
      let self = this;
      self.$refs.rentItemForm.validate((valid) => {
        if (valid) {
          if (!Util.regexPrice(self.rentItem.price)) {
            self.$message({
              message: self.$t("rules.regex", [self.$t("rentItem.price")]),
              type: "warning",
            });
            return;
          }
          if (
            !Util.regexQuantity(self.rentItem.quantity) ||
            self.rentItem.quantity === "0"
          ) {
            self.$message({
              message: self.$t("rules.regex", [self.$t("text.quantity")]),
              type: "warning",
            });
            return;
          }
          self.rentItem.color = Util.jyTrim(self.rentItem.color);
          self.rentItem.size = Util.jyTrim(self.rentItem.size);

          let selectCategoryObj = self.categories.find(
            (c) => c.id == self.rentItem.categoryId
          );
          if (selectCategoryObj != null) {
            self.rentItem.categoryName = selectCategoryObj.name;
            self.rentItem.type =
              selectCategoryObj.type == "service"
                ? selectCategoryObj.type
                : "rent";
            if (self.rentItem.type == "service") {
              self.rentItem.color = null;
              self.rentItem.size = null;
            }
          }
          self.rentItem.startAt = Util.formatDate(self.rentItem._rentAt[0]);
          self.rentItem.endAt = Util.formatDate(self.rentItem._rentAt[1]);
          self.saveDoing = true;
          RentItem.update(self.rentItem.id, self.rentItem).then(function (res) {
            self.saveDoing = false;
            if (res.status === 200) {
              self.$message({
                message: self.$t("prompt.saveSuccess"),
                type: "success",
              });
              self.$emit("updateInfo", self.rentItem);
            } else {
              self.sysError();
            }
          });
        } else {
          return false;
        }
      });
    },
  },
};
</script>